import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { PageMeta } from '../components/PageMeta';
import { JsonLd, websiteJsonLd } from '../components/JsonLd';
import type { GalleryStash, RecentStash } from '../types';

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<{ totalStashes: number; totalAssets: number } | null>(null);
  const [featuredStashes, setFeaturedStashes] = useState<GalleryStash[]>([]);
  const [recentStashes, setRecentStashes] = useState<RecentStash[]>([]);
  const [creatingDemo, setCreatingDemo] = useState(false);

  const fetchRecent = useCallback(() => {
    api.getRecentStashes(10).then(data => setRecentStashes(data.stashes)).catch(() => {});
  }, []);

  useEffect(() => {
    api.getPublicStats().then(setStats).catch(() => {});
    api.getGallery(1, 6, 'featured').then(data => setFeaturedStashes(data.stashes)).catch(() => {});
    fetchRecent();
    // Auto-refresh live feed every 60 seconds
    const interval = setInterval(fetchRecent, 60_000);
    return () => clearInterval(interval);
  }, [fetchRecent]);

  async function handleCreateDemo() {
    setCreatingDemo(true);
    try {
      const result = await api.createDemoStash();
      navigate(`/edit/${result.slug}`, { state: { token: result.token, demoPassword: result.password } });
    } catch {
      setCreatingDemo(false);
    }
  }

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-white min-h-screen transition-colors duration-300">
      <PageMeta
        title="DoStash — Save & Share Links Privately | No Account Needed"
        description="Save, organize, and share links, videos, and images in a private collection. No sign-up, no cookies, no tracking. Create your free stash in seconds."
        path="/"
      />
      <JsonLd data={websiteJsonLd()} />
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 w-full border-b border-solid border-slate-200 dark:border-slate-800 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md">
        <div className="max-w-[1280px] mx-auto flex items-center justify-between px-6 py-4">
          <a href="/" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3 no-underline cursor-pointer">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white">
              <span className="material-symbols-outlined !text-xl">shield_lock</span>
            </div>
            <h2 className="text-xl font-extrabold tracking-tight">DoStash</h2>
          </a>
          <nav className="hidden md:flex flex-1 justify-center gap-10" aria-label="Main navigation">
            <a className="text-sm font-semibold hover:text-primary transition-colors" href="#how-it-works">How it works</a>
            <a className="text-sm font-semibold hover:text-primary transition-colors" href="#features">Features</a>
            <a className="text-sm font-semibold hover:text-primary transition-colors" href="#security">Security</a>
            <a className="text-sm font-semibold hover:text-primary transition-colors" href="/explore">Explore</a>
            <a className="text-sm font-semibold hover:text-primary transition-colors" href="/blog">Blog</a>
          </nav>
          <div className="flex gap-3">
            <Link
              to="/create"
              className="flex min-w-[120px] cursor-pointer items-center justify-center rounded-lg h-10 px-5 bg-primary text-white text-sm font-bold leading-normal shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all no-underline"
            >
              Create Stash
            </Link>
          </div>
        </div>
      </header>

      <main className="flex flex-col items-center" role="main">
        {/* Hero Section */}
        <section className="w-full max-w-[1280px] px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-8 text-left">
              <div className="flex flex-col gap-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider w-fit">
                  <span className="material-symbols-outlined !text-sm">verified_user</span>
                  Anonymous &amp; Secure
                </span>
                <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-tight text-slate-900 dark:text-white">
                  Create your digital <span className="text-primary">stash</span> in seconds
                </h1>
                <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 font-normal leading-relaxed max-w-[540px]">
                  The anonymous, password-protected content aggregator. No accounts, no tracking, just your links and videos in one secure place.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/create"
                  className="flex h-14 px-8 items-center justify-center rounded-xl bg-primary text-white text-base font-bold shadow-xl shadow-primary/30 hover:bg-primary/90 transition-all no-underline"
                >
                  Create New Stash
                </Link>
                <button
                  onClick={handleCreateDemo}
                  disabled={creatingDemo}
                  className="flex h-14 px-8 items-center justify-center rounded-xl border-2 border-primary text-primary text-base font-bold hover:bg-primary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingDemo ? 'Creating...' : 'Try a Demo'}
                </button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <span className="material-symbols-outlined !text-base text-green-500">check_circle</span>
                  No Email Required
                </div>
                <div className="flex items-center gap-1">
                  <span className="material-symbols-outlined !text-base text-green-500">check_circle</span>
                  Zero Cookies
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full opacity-30"></div>
              <div className="relative w-full aspect-square md:aspect-video rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl bg-slate-900">
                <div className="w-full h-full bg-gradient-to-br from-slate-800 to-background-dark p-8 flex flex-col gap-4">
                  <div className="h-8 w-1/3 bg-slate-700 rounded-md"></div>
                  <div className="grid grid-cols-2 gap-4 flex-1">
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex flex-col gap-2">
                      <div className="h-4 w-3/4 bg-primary/30 rounded"></div>
                      <div className="h-20 w-full bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex flex-col gap-2">
                      <div className="h-4 w-1/2 bg-primary/30 rounded"></div>
                      <div className="h-20 w-full bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex flex-col gap-2">
                      <div className="h-4 w-2/3 bg-primary/30 rounded"></div>
                      <div className="h-20 w-full bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex flex-col gap-2">
                      <div className="h-4 w-3/4 bg-primary/30 rounded"></div>
                      <div className="h-20 w-full bg-slate-700/50 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Counter */}
        {stats && stats.totalStashes > 0 && (
          <section className="w-full max-w-[1280px] px-6 -mt-4 mb-8">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 py-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50">
              <div className="flex flex-col items-center gap-1">
                <span className="text-3xl md:text-4xl font-black text-primary">{stats.totalStashes.toLocaleString()}</span>
                <span className="text-sm text-slate-500 font-medium">Stashes Created</span>
              </div>
              <div className="w-px h-10 bg-slate-200 dark:bg-slate-700 hidden md:block"></div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-3xl md:text-4xl font-black text-primary">{stats.totalAssets.toLocaleString()}</span>
                <span className="text-sm text-slate-500 font-medium">Items Saved</span>
              </div>
              <div className="w-px h-10 bg-slate-200 dark:bg-slate-700 hidden md:block"></div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-3xl md:text-4xl font-black text-primary">0</span>
                <span className="text-sm text-slate-500 font-medium">Accounts Required</span>
              </div>
            </div>
          </section>
        )}

        {/* Featured Stashes */}
        {featuredStashes.length > 0 && (
          <section className="w-full max-w-[1280px] px-6 py-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight">Featured Stashes</h2>
              <Link
                to="/explore"
                className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors no-underline flex items-center gap-1"
              >
                View all
                <span className="material-symbols-outlined !text-base">arrow_forward</span>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredStashes.map((stash) => (
                <a
                  key={stash.slug}
                  href={`/${stash.slug}`}
                  className="group flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 overflow-hidden shadow-sm hover:shadow-xl hover:border-primary/30 transition-all no-underline"
                >
                  <div className="p-6 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors line-clamp-1">
                        {stash.title}
                      </h3>
                      {stash.isFeatured === 1 && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold">
                          <span className="material-symbols-outlined !text-xs">star</span>
                          Featured
                        </span>
                      )}
                    </div>
                    {stash.description && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                        {stash.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined !text-sm">folder</span>
                        {stash.assetCount} {stash.assetCount === 1 ? 'item' : 'items'}
                      </span>
                      <span>/{stash.slug}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Live Feed */}
        {recentStashes.length > 0 && (
          <section className="w-full bg-slate-50 dark:bg-slate-900/30 py-12">
            <div className="max-w-[1280px] mx-auto px-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <h2 className="text-xl md:text-2xl font-black tracking-tight">Recently Created</h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin">
                {recentStashes.map((stash) => (
                  <a
                    key={stash.slug}
                    href={`/${stash.slug}`}
                    className="shrink-0 w-[220px] flex flex-col gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-4 shadow-sm hover:shadow-lg hover:border-primary/30 transition-all no-underline group"
                  >
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors line-clamp-1">
                      {stash.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined !text-xs">folder</span>
                        {stash.assetCount}
                      </span>
                      <span>{timeAgo(stash.createdAt)}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* How it Works */}
        <section className="w-full bg-slate-50 dark:bg-slate-900/30 py-20" id="how-it-works">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="flex flex-col gap-4 mb-16 text-center items-center">
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 dark:text-white">How it works</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-[600px]">
                Building your private collection is simple and requires zero personal information. Your privacy is baked into every step.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                  <span className="material-symbols-outlined !text-3xl">edit_note</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">1. Name your stash</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Choose a unique URL for your collection to make it yours. No registration, just pick a slug and start.
                  </p>
                </div>
              </div>
              {/* Step 2 */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                  <span className="material-symbols-outlined !text-3xl">lock</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">2. Set a password</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Keep your content private with password protection. Only those with the key can edit your stash.
                  </p>
                </div>
              </div>
              {/* Step 3 */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                  <span className="material-symbols-outlined !text-3xl">share</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">3. Share and view</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Publish your stash and share the link. Anyone can view it, but only you can edit it with your password.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NEW: Why DoStash? / Value Proposition Section */}
        <section className="w-full bg-white dark:bg-background-dark py-20" id="why-dostash">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="flex flex-col gap-4 mb-16 text-center items-center">
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 dark:text-white">Is Your Digital Life a Mess?</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-[700px]">
                Tired of scattered links, privacy nightmares, and endless digital clutter? Traditional bookmarking is broken. DoStash is the simple, private, and anonymous solution to organize your digital world.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Pain Point 1: Scattered Links */}
              <div className="flex flex-col gap-6">
                <div className="w-14 h-14 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">broken_image</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">End the Link Chaos</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Stop losing valuable links across different browsers, devices, and apps. With our <strong>anonymous bookmark manager</strong>, you can aggregate all your content into one centralized, password-protected stash. Finally, a single source of truth for all your digital resources.
                  </p>
                </div>
              </div>
              {/* Pain Point 2: Privacy Invasion */}
              <div className="flex flex-col gap-6">
                <div className="w-14 h-14 rounded-xl bg-green-500/10 text-green-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">shield_person</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">Reclaim Your Privacy</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Other tools track your every click. We don't. DoStash is a <strong>privacy-focused link saver</strong> with zero cookies and no user accounts. We don't want your data. Your digital life is yours alone, and we keep it that way. No email required, ever.
                  </p>
                </div>
              </div>
              {/* Pain Point 3: Information Overload */}
              <div className="flex flex-col gap-6">
                <div className="w-14 h-14 rounded-xl bg-sky-500/10 text-sky-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">filter_alt_off</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">Focus on What Matters</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Distractions kill productivity. DoStash provides a clean, minimalist interface to organize your links without the noise. Create a focused <strong>private link collection</strong> for your research, projects, or hobbies, and access your content in a calm, organized environment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NEW: Use Cases Section */}
        <section className="w-full bg-slate-50 dark:bg-slate-900/30 py-20" id="use-cases">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="flex flex-col gap-4 mb-16 text-center items-center">
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 dark:text-white">Who is DoStash For?</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-[700px]">
                From students organizing research to developers collecting tools, DoStash is the perfect <strong>anonymous link organizer</strong> for anyone who values privacy and simplicity. See how different people use it to manage their digital lives.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Persona 1: For Researchers & Students */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">school</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">For Researchers & Students</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Create a private stash for every project. Securely organize academic papers, citations, and sources with our <strong>password-protected link saver</strong>. Share your research with study groups using a simple, private link.
                  </p>
                </div>
              </div>
              {/* Persona 2: For Developers & Techies */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">code</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">For Developers & Techies</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Tired of scattered documentation and tutorials? Use DoStash as your go-to <strong>developer resource organizer</strong>. Create stashes for different languages, frameworks, or projects. It's the perfect, no-fuss tool for your tech stack.
                  </p>
                </div>
              </div>
              {/* Persona 3: For Content Curators */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">interests</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">For Content Curators</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Build and share beautiful collections of links without giving up your privacy. Our <strong>anonymous content curation</strong> tool lets you share your expertise with the world, without accounts or tracking. Your content, your control.
                  </p>
                </div>
              </div>
              {/* Persona 4: For the Privacy-Conscious */}
              <div className="group flex flex-col gap-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark p-8 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all">
                <div className="w-14 h-14 rounded-xl bg-teal-500/10 text-teal-500 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-3xl">privacy_tip</span>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-extrabold">For the Privacy-Conscious</h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    If you believe your data is your own, DoStash is for you. It's a <strong>no account bookmark tool</strong> built from the ground up for privacy. We don't use cookies, we don't track you, and we don't ask for your email. Period.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full max-w-[1280px] px-6 py-20" id="features">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold tracking-tight">Your stash, your rules</h2>
            <div className="hidden md:block h-[1px] flex-1 mx-8 bg-slate-200 dark:bg-slate-800"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="flex flex-col gap-6 group">
              <div className="w-full aspect-video rounded-2xl overflow-hidden bg-slate-200 dark:bg-slate-800 relative group-hover:scale-[1.01] transition-transform duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-slate-900/80 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-6xl text-white opacity-80">link</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="text-2xl font-bold text-slate-900 dark:text-white">Link Aggregation</h4>
                <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed">
                  Store any URL or video from across the web in one organized view. Supports YouTube, Vimeo, TikTok, Dailymotion, Loom, Twitch, images, and any website.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-6 group">
              <div className="w-full aspect-video rounded-2xl overflow-hidden bg-slate-200 dark:bg-slate-800 relative group-hover:scale-[1.01] transition-transform duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/40 to-slate-900/80 flex items-center justify-center">
                  <span className="material-symbols-outlined !text-6xl text-white opacity-80">fingerprint</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <h4 className="text-2xl font-bold text-slate-900 dark:text-white">Privacy First</h4>
                <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed">
                  No cookies, no trackers, and no personal data collection ever. We don't want to know who you are, just what you want to save.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="w-full max-w-[1280px] px-6 py-20" id="security">
          <div className="w-full rounded-[2rem] bg-primary p-8 md:p-16 text-center text-white flex flex-col items-center gap-8 shadow-2xl shadow-primary/40 relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <h2 className="text-3xl md:text-5xl font-black max-w-[700px] leading-tight z-10">
              Ready to declutter your web experience?
            </h2>
            <p className="text-white/80 text-lg md:text-xl max-w-[600px] font-medium z-10">
              Start building your collection right now. No sign-up required, no strings attached.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 z-10">
              <Link
                to="/create"
                className="flex h-14 px-8 items-center justify-center rounded-xl bg-white text-primary text-base font-bold shadow-xl hover:bg-slate-50 transition-all no-underline"
              >
                Create Your Free Stash
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full border-t border-slate-200 dark:border-slate-800 py-12 bg-white dark:bg-background-dark">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-3 no-underline">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white">
                <span className="material-symbols-outlined !text-xl">shield_lock</span>
              </div>
              <h2 className="text-xl font-extrabold tracking-tight">DoStash</h2>
            </Link>
            <p className="text-slate-500 max-w-sm">
              The simplest way to curate and share content without sacrificing your digital privacy. Made for the open web.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <h5 className="font-bold">Product</h5>
            <nav className="flex flex-col gap-2 text-slate-500" aria-label="Product links">
              <Link to="/create" className="hover:text-primary transition-colors">Create Stash</Link>
              <a className="hover:text-primary transition-colors" href="#features">Features</a>
              <a className="hover:text-primary transition-colors" href="#how-it-works">How it Works</a>
              <Link to="/explore" className="hover:text-primary transition-colors">Explore</Link>
              <a className="hover:text-primary transition-colors" href="/blog">Blog</a>
            </nav>
          </div>
          <div className="flex flex-col gap-4">
            <h5 className="font-bold">Legal & Support</h5>
            <nav className="flex flex-col gap-2 text-slate-500" aria-label="Legal and support links">
              <Link to="/terms" className="hover:text-primary transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy</Link>
              <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>
            </nav>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 mt-12 pt-8 border-t border-slate-100 dark:border-slate-800 text-center text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} DoStash. Built for privacy.
        </div>
      </footer>
    </div>
  );
}
